/**
 * Update date: 2024-05-18
 * Screen 2052.C
 */
import React, { useRef } from 'react';
import { DataGridFull } from 'app/components/DataGrid';
import useFormat from 'hooks/useFormat';
import moment from 'moment';
import { useModuleContext } from '..';
import LoadPanel from 'app/components/LoadPanel';
import useFormatTime from 'hooks/useFormatTime';
import useDictionary from 'hooks/useDictionary';

function InventoryInquiryLocationTable() {
  const { t } = useDictionary({ programId: '2052.C' });
  const { DateFormat } = useFormatTime();
  const { QtyFormat, DoubleFormat, IntegerFormat } = useFormat();
  const dataGridRef: any = useRef(null);
  const context: any = useModuleContext();
  const { inventoryData, loadingInventoryData } = context;

  const columns: any = [
    {
      caption: t('Location'),
      alignment: 'center',
      items: [
        {
          dataField: 'zone_nm',
          caption: t('Zone'),
          showWhenGrouped: true,
        },
        {
          dataField: 'location_cd',
          caption: t('Location Code'),
          showWhenGrouped: true,
        },
        {
          dataField: 'location_nm',
          caption: t('Location'),
          showWhenGrouped: true,
        },
        {
          dataField: 'location_route_order',
          caption: t('Route Order'),
          dataType: 'number',
          format: IntegerFormat,
          showWhenGrouped: true,
        },
        {
          dataField: 'location_load_tp_nm',
          caption: t('Load Type'),
          showWhenGrouped: true,
        },
        {
          dataField: 'location_distribution_period_dm',
          caption: t('Distribution Period'),
          dataType: 'date',
          format: DateFormat,
          showWhenGrouped: true,
        },
      ],
    },
    {
      caption: t('Product'),
      alignment: 'center',
      columns: [
        {
          dataField: 'product_cd',
          caption: t('Product Code'),
          showWhenGrouped: true,
        },
        {
          dataField: 'product_nm',
          caption: t('Product'),
          showWhenGrouped: true,
        },
        {
          dataField: 'product_maker_nm',
          caption: t('Maker'),
          showWhenGrouped: true,
        },
        {
          dataField: 'marketable_size',
          caption: t('Marketable Size'),
          showWhenGrouped: true,
        },
        {
          dataField: 'vendor_nm_default',
          caption: t('Default Vendor'),
          showWhenGrouped: true,
        },
        {
          dataField: 'product_cost',
          caption: t('Product Cost'),
          showWhenGrouped: true,
          dataType: 'number',
          format: DoubleFormat,
        },
        {
          dataField: 'stock_ea',
          caption: t('Stock Ea'),
          showWhenGrouped: true,
          dataType: 'number',
          format: QtyFormat,
        },
        {
          caption: t('Order Allocation'),
          dataField: 'curr_order_allocation',
          dataType: 'number',
          format: QtyFormat,
          showWhenGrouped: true,
        },
        {
          caption: t('Actual Stock'),
          dataField: 'actual_atock',
          showWhenGrouped: true,
          dataType: 'number',
          format: QtyFormat,
        },
        {
          dataField: 'product_origin_nm',
          caption: t('Origin'),
          showWhenGrouped: true,
          visible: false,
        },
        {
          dataField: 'large_category_nm',
          caption: t('Large Category'),
          showWhenGrouped: true,
          visible: false,
        },
        {
          dataField: 'medium_category_nm',
          caption: t('Medium Category'),
          showWhenGrouped: true,
          visible: false,
        },
        {
          dataField: 'small_category_nm',
          caption: t('Small Category'),
          showWhenGrouped: true,
          visible: false,
        },
        {
          dataField: 'sub_category_nm',
          caption: t('Sub Category'),
          showWhenGrouped: true,
          visible: false,
        },
      ],
    },
    {
      caption: t('ESl Custom'),
      alignment: 'center',
      visible: false,
      columns: [
        {
          dataField: 'option_property01',
          caption: t('Internal Product ID'),
          showWhenGrouped: true,
        },
        {
          dataField: 'option_property02',
          caption: t('Internal Product Name'),
          showWhenGrouped: true,
        },
        {
          dataField: 'option_property03',
          caption: t('Internal Option ID'),
          showWhenGrouped: true,
        },
        {
          dataField: 'option_property04',
          caption: t('Internal Option Name'),
          showWhenGrouped: true,
        },
        {
          dataField: 'item_01',
          caption: t('Additional Information ID'),
          showWhenGrouped: true,
        },
        {
          dataField: 'item_04',
          caption: t('ESL Product Name'),
          showWhenGrouped: true,
        },
        {
          dataField: 'item_05',
          caption: t('ESL Comment'),
          showWhenGrouped: true,
        },
        {
          dataField: 'item_06',
          caption: t('ESL Bedge'),
          showWhenGrouped: true,
        },
      ],
    },
  ];

  /**
   * on export excel
   *
   */
  const exportExcel = () => {
    dataGridRef?.current?.instance?.exportToExcel();
  };

  const exportFileName =
    'SMDC_2052.C Inventory inquiry by location_' + moment().format('YYYYMMDD');

  return (
    <>
      <LoadPanel visible={loadingInventoryData} />
      <DataGridFull
        storageKeyInquiry="2052.C_v2"
        columns={columns}
        ref={dataGridRef}
        dataSource={inventoryData}
        options={{
          groupPanel: { visible: true },
          columnAutoWidth: true,
          scrolling: {
            columnRenderingMode: 'virtual',
          },
          onToolbarPreparing: (e: any) => {
            e.toolbarOptions.items.unshift(
              {
                location: 'before',
                template: 'totalCount',
              },
              {
                location: 'before',
                widget: 'dxButton',
                options: {
                  icon: 'xlsxfile',
                  text: t('Export Excel'),
                  disabled: !inventoryData.length,
                  onClick: () => exportExcel(),
                },
              },
            );
          },
          export: {
            fileName: exportFileName,
          },
          summary: {
            totalItems: [
              {
                name: 'Stock Ea',
                column: 'stock_ea',
                valueFormat: QtyFormat,
                displayFormat: '{0}',
                summaryType: 'sum',
              },
              {
                column: 'actual_atock',
                valueFormat: QtyFormat,
                displayFormat: '{0}',
                summaryType: 'sum',
              },
              {
                column: 'curr_order_allocation',
                valueFormat: QtyFormat,
                displayFormat: '{0}',
                summaryType: 'sum',
              },
            ],
            groupItems: [
              {
                summaryType: 'count',
                displayFormat: '{0}',
              },
              {
                column: 'stock_ea',
                valueFormat: QtyFormat,
                displayFormat: '{0}',
                summaryType: 'sum',
                alignByColumn: true,
                showInGroupFooter: false,
              },
              {
                column: 'actual_atock',
                valueFormat: QtyFormat,
                displayFormat: '{0}',
                summaryType: 'sum',
                alignByColumn: true,
                showInGroupFooter: false,
              },
              {
                column: 'curr_order_allocation',
                valueFormat: QtyFormat,
                displayFormat: '{0}',
                summaryType: 'sum',
                alignByColumn: true,
                showInGroupFooter: false,
              },
            ],
          },
        }}
      />
    </>
  );
}

export default InventoryInquiryLocationTable;
